import type { TemplateInputValue, TemplateRef } from "types/template.types"

/**
 * The assistant's shared contract with the AI-Converter
 * (`AI-Converter/src/services/assistant/contract.js`). Field names are the
 * same on both sides; change them together.
 */

/** The screen a turn is sent from. */
export type AssistantScreen = "editor" | "tables" | "dashboards" | "console"

/** One message of the conversation as it is sent to the converter. */
export interface AssistantTurnMessage {
  role: "user" | "assistant"
  text: string
}

export interface AssistantTurnRequest {
  projectId: string
  owner: string
  screen: AssistantScreen
  /** The last 20 messages, oldest first; the newest is the user's question. */
  messages: AssistantTurnMessage[]
  context: {
    qnr?: {
      /** "" is the master. */
      variant: string
      /** The editor's paragraphs; line n is `lines[n - 1]`. */
      lines: string[]
      selection?: { fromLine: number; toLine: number }
    }
    /** `document` is a TabSpecDocument. */
    tabSpec?: { name: string; document: unknown; baseHash: string }
    /** `document` is a DashboardDocument. */
    dashboard?: { name: string; document: unknown; baseHash: string }
  }
}

/**
 * The check a proposal passed (or failed) before it was sent. `unchecked`:
 * the check could not run (DATA(Q) busy); `ok` is then false and `errors`
 * say why, but the proposal is not known to fail.
 */
export interface Validation {
  ok: boolean
  errors: { line?: number; message: string }[]
  unchecked?: true
}

/** One line-range replacement; `toLine = fromLine - 1` inserts before `fromLine`. */
export interface QnrEdit {
  fromLine: number
  toLine: number
  oldLines: string[]
  newLines: string[]
  /** Respondent wording (question text, answer labels, lists or order) changed. */
  wording: boolean
}

export type Proposal =
  | {
      id: string
      kind: "qnr-edit"
      variant: string
      edits: QnrEdit[]
      validation: Validation
    }
  | {
      id: string
      kind: "template-fill"
      block: {
        ref: TemplateRef
        title: string
        inputs: TemplateInputValue[]
        exceptions: string[]
      }
      validation: Validation
    }
  | {
      id: string
      kind: "tabspec"
      name: string
      document: unknown
      baseHash: string
      changedTableIds: string[]
      /** A saved query the document uses that may not be saved yet (a script proposed with it). */
      requiresScript?: string
      /** Tables of the open spec that the proposal drops. */
      removedTableIds?: string[]
      validation: Validation
    }
  | {
      id: string
      kind: "dashboard"
      name: string
      document: unknown
      baseHash: string
      changedTileIds: string[]
      /** Tiles of the open dashboard that the proposal drops. */
      removedTileIds?: string[]
      validation: Validation
    }
  | {
      id: string
      kind: "script"
      name: string
      script: string
      validation: Validation
    }

export type ProposalKind = Proposal["kind"]

/** The proposal of one kind, e.g. `ProposalOf<"tabspec">`. */
export type ProposalOf<K extends ProposalKind> = Extract<Proposal, { kind: K }>

export const PROPOSAL_KINDS: readonly ProposalKind[] = [
  "qnr-edit",
  "template-fill",
  "tabspec",
  "dashboard",
  "script",
]

export type AssistantErrorCode = "token-expired" | "too-large" | "disabled" | "error"

export const ASSISTANT_ERROR_CODES: readonly AssistantErrorCode[] = [
  "token-expired",
  "too-large",
  "disabled",
  "error",
]

export interface AssistantTurnResponse {
  success: boolean
  code?: AssistantErrorCode
  data?: { reply: string; proposals: Proposal[]; rounds: number }
  message?: string
}

/** `GET /assistant-status`: whether the user's organisation has the assistant. */
export interface AssistantStatus {
  enabled: boolean
}
